// 텍스트 레이어에 GHS 코드나 한글 라벨이 없는 MSDS PDF를 위한 보강 경로.
// msdsPdfParser.js 의 parsePictograms() 는 텍스트만 보므로, 실제 제조사 PDF에 흔한
// "그림문자를 이미지로만 넣고 텍스트 캡션이 없는" 문서에서는 아무것도 잡지 못한다.
// 이 모듈은 PDF 페이지에서 픽토그램처럼 생긴 이미지를 찾아 GHS 웹앱 에셋과 해시 비교로 매칭한다.
//
// 검증 근거(실측): 샘플 MSDS PDF 1건(그림문자 GHS07 이미지만 있고 텍스트 캡션 없음)에서
// 레퍼런스 최소거리 201, 매칭 거리 21, 2등과 격차 214 — 임계값 대비 여유가 크다.
// 단, 이 실측은 발행처 1곳 기준이므로 MAX_DISTANCE/MIN_MARGIN 은 다른 발행처 샘플이
// 쌓이면 재조정이 필요할 수 있다.
import { OPS } from "pdfjs-dist/legacy/build/pdf";
import GHS_HASHES from "./generated/ghsHashes.json";
import { toBits, hamming, compositeToWhiteRGB, bbox, MAX_DISTANCE, MIN_MARGIN, BBOX_THRESHOLD } from "./ghsHashCore";

const MIN_SIZE = 40; // 이보다 작으면 로고/장식 이미지로 간주해 제외
// 이보다 크면 스캔된 전체 페이지/사진으로 간주해 제외. 픽토그램은 인쇄 해상도에서도 수백 px 이고,
// 해시 계산(toBits/bbox)이 픽셀 수에 비례하는 동기 루프라 수천 px 이미지는 메인 스레드를 수 초씩 잡아 화면을 멈추게 한다.
const MAX_SIZE = 1500;
const MAX_ASPECT = 1.4; // 픽토그램은 정사각형에 가까움 (가로/세로 비율 필터)
// 스캔본(텍스트 레이어 없이 페이지 전체가 이미지인 PDF)은 페이지 래스터 안에서 빨간 마름모를 찾아 그림문자를 잘라낸다.
// 페이지 이미지는 이 크기까지 줄여서 다루고(600dpi A4 스캔도 1cm 그림문자가 40px 이상 남는다),
// 이보다 픽셀 수가 많은 이미지는 메모리 때문에 아예 디코딩 결과를 쓰지 않는다.
const PAGE_MAX_SIZE = 2400;
const MAX_IMAGE_PIXELS = 30 * 1000 * 1000;
const PAGE_MIN_SIZE = 400; // 양변이 이보다 크면 "페이지 같은" 이미지로 보고 마름모 탐색을 돌린다

// 여러 페이지에 반복되는 이미지(머리글 로고 등)는 "g_" 접두어가 붙어 page.objs 가 아니라
// page.commonObjs 에 저장된다. objs.get(name, callback) 은 해당 저장소에 객체가 들어올 때까지
// 콜백을 미루므로, 엉뚱한 저장소에서 찾으면 Promise 가 영원히 끝나지 않아 업로드 화면이 멈춘다.
// 그래서 접두어로 저장소를 고르고, 아직 resolve 되지 않은 객체는 기다리지 않고 건너뛴다.
function getPageObject(page, name) {
  const store = name.startsWith("g_") ? page.commonObjs : page.objs;
  if (!store.has(name)) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      store.get(name, resolve);
    } catch (e) {
      resolve(null);
    }
  });
}

const WHITE_THRESHOLD = 245; // 이 값 이상이면 흰 배경 픽셀로 본다(ghsHashCore.bbox 와 동일 기준)

// ---------------------------------------------------------------------------
// 1) 페이지 안에서 이미지가 그려진 위치(CTM) 추적
//
// 일부 MSDS 작성 도구는 그림문자 하나를 여러 조각 이미지로 쪼개서 넣는다(실측):
//   - 위쪽 절반/아래쪽 절반 두 장(588x294 x2)
//   - 가로 1픽셀짜리 띠 1,000여 장(마름모 모양이라 폭이 줄마다 다름)
// 조각 각각은 크기/비율 필터에서 탈락하므로, 그려진 위치를 따라가 서로 맞닿은 조각들을
// 하나의 래스터로 다시 합쳐(compositeGroup) 원래 그림문자를 복원한다.
// ---------------------------------------------------------------------------

// pdf.js Util.transform 과 같은 순서: 기존 CTM 뒤에 m 을 덧붙인다(점 p → old(m(p)))
function composeMatrix(old, m) {
  return [
    (old[0] * m[0]) + (old[2] * m[1]),
    (old[1] * m[0]) + (old[3] * m[1]),
    (old[0] * m[2]) + (old[2] * m[3]),
    (old[1] * m[2]) + (old[3] * m[3]),
    (old[0] * m[4]) + (old[2] * m[5]) + old[4],
    (old[1] * m[4]) + (old[3] * m[5]) + old[5],
  ];
}

// 단위 사각형(이미지 영역)이 CTM 으로 놓이는 페이지상 사각형. y 는 아래로 증가하도록 뒤집어
// 이미지 0번째 행이 top 에 오게 맞춘다(pdf.js 는 이미지 윗변을 y=1 에 그린다).
function imageRect(ctm) {
  const xs = [];
  const ys = [];
  for (const [x, y] of [[0, 0], [1, 0], [0, 1], [1, 1]]) {
    xs.push((ctm[0] * x) + (ctm[2] * y) + ctm[4]);
    ys.push((ctm[1] * x) + (ctm[3] * y) + ctm[5]);
  }
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = -Math.max(...ys);
  const bottom = -Math.min(...ys);
  return { left, top, right, bottom, w: right - left, h: bottom - top };
}

// 페이지의 그리기 명령을 순서대로 따라가며 각 이미지 그리기의 (디코딩된 이미지, 페이지상 위치)를 모은다
async function collectPlacedImages(page, pageNum) {
  const ops = await page.getOperatorList();
  const cache = new Map(); // name → 디코딩된 이미지(같은 이미지를 여러 번 그리는 문서가 많다)
  const placed = [];
  let ctm = [1, 0, 0, 1, 0, 0];
  const stack = [];
  for (let i = 0; i < ops.fnArray.length; i++) {
    const fn = ops.fnArray[i];
    const args = ops.argsArray[i];
    if (fn === OPS.save) {
      stack.push(ctm);
    } else if (fn === OPS.restore) {
      if (stack.length) ctm = stack.pop();
    } else if (fn === OPS.transform) {
      ctm = composeMatrix(ctm, args);
    } else if (fn === OPS.paintFormXObjectBegin) {
      stack.push(ctm);
      if (args && args[0]) ctm = composeMatrix(ctm, args[0]);
    } else if (fn === OPS.paintFormXObjectEnd) {
      if (stack.length) ctm = stack.pop();
    } else if (fn === OPS.paintImageXObject || fn === OPS.paintInlineImageXObject) {
      // 인라인 이미지(BI ... EI)는 이름 없이 디코딩된 이미지 객체가 인자로 바로 온다.
      // 실측: 그림문자를 1px 띠 300여 장의 인라인 이미지로 쪼개 넣은 MSDS(암모니아수) — 조각 합성 대상이다.
      const inline = fn === OPS.paintInlineImageXObject;
      const name = inline ? `inline@${i}` : args[0];
      if (!cache.has(name)) {
        const img = inline ? args[0] : await getPageObject(page, name);
        const usable = img && img.width && img.height && img.data && img.width * img.height <= MAX_IMAGE_PIXELS;
        cache.set(name, usable ? img : null);
      }
      const img = cache.get(name);
      if (!img) continue;
      const rect = imageRect(ctm);
      if (!(rect.w > 0) || !(rect.h > 0)) continue;
      placed.push({ pageNum, name, img, rect });
    }
  }
  return placed;
}

// ---------------------------------------------------------------------------
// 2) 맞닿은 조각 묶기 + 하나의 래스터로 합성
// ---------------------------------------------------------------------------

function unionRect(a, b) {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  const right = Math.max(a.right, b.right);
  const bottom = Math.max(a.bottom, b.bottom);
  return { left, top, right, bottom, w: right - left, h: bottom - top };
}

// 조각 r 이 묶음 영역 u 와 맞닿아 있는가(페이지 단위 기준 약간의 여유를 둔다)
function touches(u, r) {
  const tol = Math.max(1, Math.min(u.w, u.h) * 0.02);
  return r.left <= u.right + tol && r.right >= u.left - tol && r.top <= u.bottom + tol && r.bottom >= u.top - tol;
}

// {width, height} 가 그림문자 크기·비율 범위 안인가
function aspectOf(pixels) {
  return pixels.width / pixels.height;
}

function isPictogramShaped(pixels) {
  const aspect = aspectOf(pixels);
  if (pixels.width < MIN_SIZE || pixels.height < MIN_SIZE) return false;
  if (pixels.width > MAX_SIZE || pixels.height > MAX_SIZE) return false;
  return aspect <= MAX_ASPECT && aspect >= 1 / MAX_ASPECT;
}

// 그 자체로 그림문자 크기·비율인 이미지는 조각이 아니므로 단독으로 비교한다(합치면 옆의 그림문자와
// 묶여 다시 잘라야 하고, 그 과정의 리샘플링으로 거리가 나빠진다 — 실측: 질산.pdf 101px 3장).
function isFragment(item) {
  const { width, height } = item.img;
  // 페이지 크기 이미지는 모양이 정사각형에 가까워도 그림문자가 아니라 페이지 조각이다
  // (실측: 1240x1323 + 1240x430 두 장으로 나뉜 스캔 페이지 — 그림문자가 경계에 걸쳐 잘려 있어 합쳐야 한다)
  if (width >= PAGE_MIN_SIZE && height >= PAGE_MIN_SIZE) return true;
  return !isPictogramShaped({ width, height });
}

// 두 사각형이 겹치는 넓이가 작은 쪽 넓이의 몇 배인가(0~1)
function overlapRatio(a, b) {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  if (w <= 0 || h <= 0) return 0;
  return (w * h) / Math.min(a.w * a.h, b.w * b.h);
}
const OVERLAY_MIN_OVERLAP = 0.8;

// 그린 순서대로 서로 맞닿은 조각 이미지들을 한 묶음으로 만든다. 조각이 아닌 이미지는 묶음을 끊되,
// 같은 자리에 겹쳐 그린 이미지(실측: 빈 마름모 테두리 한 장 + 심벌 한 장을 같은 위치에 겹쳐 놓은 MSDS)는
// 하나로 합성해야 그림문자가 되므로 겹침 묶음으로 묶는다.
function groupPlacedImages(placed) {
  const groups = [];
  let group = null;
  for (const item of placed) {
    if (!isFragment(item)) {
      if (group && group.overlay && overlapRatio(group.rect, item.rect) >= OVERLAY_MIN_OVERLAP) {
        group.items.push(item);
        group.rect = unionRect(group.rect, item.rect);
        continue;
      }
      group = { items: [item], rect: item.rect, overlay: true };
      groups.push(group);
      continue;
    }
    if (group && !group.overlay && touches(group.rect, item.rect)) {
      group.items.push(item);
      group.rect = unionRect(group.rect, item.rect);
      continue;
    }
    group = { items: [item], rect: item.rect };
    groups.push(group);
  }
  return groups;
}

// 묶음을 합성했을 때의 픽셀/페이지단위 배율: 조각 중 가장 촘촘한 것에 맞추되 MAX_SIZE 를 넘지 않게 한다
function groupScale(group) {
  let scale = 0;
  for (const item of group.items) {
    scale = Math.max(scale, item.img.width / item.rect.w, item.img.height / item.rect.h);
  }
  return Math.min(scale, PAGE_MAX_SIZE / group.rect.w, PAGE_MAX_SIZE / group.rect.h);
}

// 긴 변이 maxDim 을 넘는 픽셀 묶음을 최근접 보간으로 줄인다(페이지 스캔 이미지용)
function downscale(pixels, maxDim) {
  const { width, height, data } = pixels;
  const factor = Math.max(width, height) / maxDim;
  if (factor <= 1) return pixels;
  const w = Math.max(1, Math.round(width / factor));
  const h = Math.max(1, Math.round(height / factor));
  const out = new Uint8ClampedArray(w * h * 3);
  for (let y = 0; y < h; y++) {
    const sy = Math.min(height - 1, Math.floor(y * factor));
    for (let x = 0; x < w; x++) {
      const sx = Math.min(width - 1, Math.floor(x * factor));
      const si = ((sy * width) + sx) * 3;
      const di = ((y * w) + x) * 3;
      out[di] = data[si];
      out[di + 1] = data[si + 1];
      out[di + 2] = data[si + 2];
    }
  }
  return { data: out, width: w, height: h, channels: 3 };
}

// 묶음의 조각들을 페이지상 위치대로 한 장의 흰 배경 RGB 래스터에 그린다(최근접 보간)
function compositeGroup(group, scale) {
  const { items, rect } = group;
  const width = Math.max(1, Math.round(rect.w * scale));
  const height = Math.max(1, Math.round(rect.h * scale));
  const data = new Uint8ClampedArray(width * height * 3).fill(255);
  for (const item of items) {
    // 조각이 RGBA(kind 3)면 알파로 섞어 그린다. 흰 배경으로 먼저 평탄화하면 겹쳐 놓인 조각의 투명 영역이
    // 먼저 그린 조각을 흰색으로 덮어 심벌 가운데가 지워진다(실측: 가우징봉 MSDS, 조각 18장이 겹침).
    const { width: sw, height: sh, kind, data: sdata } = item.img;
    if (kind !== 2 && kind !== 3) continue;
    const channels = kind === 3 ? 4 : 3;
    const x0 = Math.round((item.rect.left - rect.left) * scale);
    const y0 = Math.round((item.rect.top - rect.top) * scale);
    const x1 = Math.min(width, Math.max(x0 + 1, Math.round((item.rect.right - rect.left) * scale)));
    const y1 = Math.min(height, Math.max(y0 + 1, Math.round((item.rect.bottom - rect.top) * scale)));
    const dw = x1 - x0;
    const dh = y1 - y0;
    for (let y = y0; y < y1; y++) {
      const sy = Math.min(sh - 1, Math.floor(((y - y0) / dh) * sh));
      for (let x = x0; x < x1; x++) {
        const sx = Math.min(sw - 1, Math.floor(((x - x0) / dw) * sw));
        const si = ((sy * sw) + sx) * channels;
        const di = ((y * width) + x) * 3;
        const alpha = kind === 3 ? sdata[si + 3] / 255 : 1;
        if (alpha <= 0) continue;
        data[di] = (data[di] * (1 - alpha)) + (sdata[si] * alpha);
        data[di + 1] = (data[di + 1] * (1 - alpha)) + (sdata[si + 1] * alpha);
        data[di + 2] = (data[di + 2] * (1 - alpha)) + (sdata[si + 2] * alpha);
      }
    }
  }
  return { data, width, height, channels: 3 };
}

// ---------------------------------------------------------------------------
// 3) 가로로 긴 이미지 나누기
//
// 그림문자 여러 개를 한 줄로 이어 붙인 띠 이미지(실측: 521x126 에 그림문자 3개)는
// 잉크가 없는 세로 빈 칸을 경계로 잘라 각각을 후보로 삼는다. 빈 칸 없이 붙어 있으면 비율만큼 등분한다.
// ---------------------------------------------------------------------------

function isInk(data, i) {
  return data[i] < WHITE_THRESHOLD || data[i + 1] < WHITE_THRESHOLD || data[i + 2] < WHITE_THRESHOLD;
}

function cropPixels(pixels, x, y, w, h) {
  const data = new Uint8ClampedArray(w * h * 3);
  for (let yy = 0; yy < h; yy++) {
    const si = (((y + yy) * pixels.width) + x) * 3;
    data.set(pixels.data.subarray(si, si + (w * 3)), yy * w * 3);
  }
  return { data, width: w, height: h, channels: 3 };
}

// [x0, x1) 열 범위 안에서 잉크가 있는 행 범위로 잘라낸 조각
function trimRows(pixels, x0, x1) {
  let top = pixels.height;
  let bottom = -1;
  for (let y = 0; y < pixels.height; y++) {
    for (let x = x0; x < x1; x++) {
      if (isInk(pixels.data, ((y * pixels.width) + x) * 3)) {
        if (y < top) top = y;
        bottom = y;
        break;
      }
    }
  }
  if (bottom < 0) return null;
  return cropPixels(pixels, x0, top, x1 - x0, bottom - top + 1);
}

function splitWideImage(pixels) {
  const { width, height, data } = pixels;
  const inkCols = new Uint8Array(width);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!inkCols[x] && isInk(data, ((y * width) + x) * 3)) inkCols[x] = 1;
    }
  }
  const runs = [];
  let start = -1;
  for (let x = 0; x <= width; x++) {
    const ink = x < width && inkCols[x];
    if (ink && start < 0) start = x;
    if (!ink && start >= 0) {
      // 세로 길이의 30% 도 안 되는 좁은 구간은 가장자리 노이즈/구분선으로 보고 버린다(실측: JPEG 띠의 1px 잡티가
      // 구간을 3개로 만들어 진짜 그림문자 구간이 등분되지 못했다)
      if (x - start >= height * 0.3) runs.push([start, x]);
      start = -1;
    }
  }
  if (!runs.length) runs.push([0, width]);
  // 구간이 여전히 가로로 길면(그림문자들이 붙어 있으면) 그 구간을 가로/세로 비율만큼 등분한다
  const tiles = [];
  for (const [x0, x1] of runs) {
    const w = x1 - x0;
    const n = w / height > MAX_ASPECT ? Math.max(2, Math.round(w / height)) : 1;
    const tileW = Math.floor(w / n);
    for (let t = 0; t < n; t++) tiles.push(trimRows(pixels, x0 + (t * tileW), x0 + ((t + 1) * tileW)));
  }
  return tiles.filter(Boolean);
}

// ---------------------------------------------------------------------------
// 4) 후보 수집
// ---------------------------------------------------------------------------

// 세로는 그림문자 크기 범위인데 가로로만 긴 이미지(그림문자 여러 개를 한 줄로 넣은 띠)
function isWideStrip(pixels) {
  return pixels.height >= MIN_SIZE && pixels.height <= MAX_SIZE && pixels.width <= MAX_SIZE && aspectOf(pixels) > MAX_ASPECT;
}

// ---------------------------------------------------------------------------
// 테두리 게이트
//
// 해시 거리만으로는 그림문자와 무관한 이미지가 우연히 가까워질 수 있다(실측: 시멘트 회사 원형 로고가
// 부식성 변형 템플릿과 거리 75). GHS 그림문자는 예외 없이 빨간(흑백 인쇄면 검은) 마름모 테두리를 갖고,
// 일부 서식은 빨간 사각 테두리를 쓰므로, 테두리 선을 따라 점을 찍어 그 색이 맞는지 먼저 확인한다.
// ---------------------------------------------------------------------------
const FRAME_SAMPLES = 48; // 테두리 한 바퀴를 따라 찍는 점 수
const FRAME_INSET = 0.035; // bbox 바깥선에서 안쪽으로 들어가는 비율(57px 이미지도 4px 테두리 안에 들어온다)
const FRAME_MIN_RATIO = 0.6; // 이 비율 이상의 점이 테두리 색이어야 한다
const FRAME_MIN_DARK_RATIO = 0.8; // 빨간 점 없이 검은 점만으로 테두리를 인정할 때의 비율
const FRAME_SEARCH_RADIUS = 0.02; // 둘레 점마다 bbox 짧은 변의 이 비율만큼 이웃을 함께 본다(가는·비뚤어진 스캔 테두리)

function pixelColorAt(data, width, x, y) {
  const i = ((y * width) + x) * 3;
  const r = data[i];
  const g = data[i + 1];
  const b = data[i + 2];
  if (r >= 110 && g <= 120 && b <= 120 && r - Math.max(g, b) >= 50) return "red";
  if ((0.299 * r) + (0.587 * g) + (0.114 * b) < 90) return "dark";
  return null;
}

// 점 (x, y) 주변 radius 안에 테두리 색 픽셀이 있으면 그 색. 스캔본의 가는(1~3px)·비뚤어진 테두리는
// 정확한 한 점만 보면 자주 비껴가므로 작은 이웃을 함께 본다(빨강 우선).
function frameColorAt(data, width, height, x, y, radius) {
  const xi = Math.round(x);
  const yi = Math.round(y);
  let dark = false;
  for (let dy = -radius; dy <= radius; dy++) {
    const yy = yi + dy;
    if (yy < 0 || yy >= height) continue;
    for (let dx = -radius; dx <= radius; dx++) {
      const xx = xi + dx;
      if (xx < 0 || xx >= width) continue;
      const color = pixelColorAt(data, width, xx, yy);
      if (color === "red") return "red";
      if (color === "dark") dark = true;
    }
  }
  return dark ? "dark" : null;
}

// 점 목록을 따라 색을 세어 { red, dark } 비율을 돌려준다
function frameRatios(pixels, points, radius) {
  const { data, width, height } = pixels;
  let red = 0;
  let dark = 0;
  for (const [x, y] of points) {
    const color = frameColorAt(data, width, height, x, y, radius);
    if (color === "red") red += 1;
    else if (color === "dark") dark += 1;
  }
  return { red: red / points.length, dark: dark / points.length };
}

// 네 꼭짓점을 잇는 닫힌 다각형 둘레를 FRAME_SAMPLES 개 점으로 나눈다
function perimeterPoints(corners) {
  const points = [];
  const perEdge = FRAME_SAMPLES / corners.length;
  for (let e = 0; e < corners.length; e++) {
    const [x0, y0] = corners[e];
    const [x1, y1] = corners[(e + 1) % corners.length];
    for (let k = 0; k < perEdge; k++) {
      const t = (k + 0.5) / perEdge;
      points.push([x0 + ((x1 - x0) * t), y0 + ((y1 - y0) * t)]);
    }
  }
  return points;
}

// "diamond" | "square" | null
function frameTypeOf(pixels) {
  const box = bbox(pixels.data, pixels.width, pixels.height, 3, BBOX_THRESHOLD);
  // 내용물(bbox)이 정사각형에 가깝지 않으면 테두리를 재지 않는다. 이미지 자체는 정사각형이어도 안에 든 것이
  // 가로로 긴 로고(실측: 287x205 이미지 속 283x80 DuPont 로고)인 경우 검은 글자가 마름모 둘레에 걸려 통과했다.
  const boxAspect = box.w / box.h;
  if (boxAspect > MAX_ASPECT || boxAspect < 1 / MAX_ASPECT) return null;
  const cx = box.x + (box.w / 2);
  const cy = box.y + (box.h / 2);
  const hw = (box.w / 2) * (1 - (2 * FRAME_INSET));
  const hh = (box.h / 2) * (1 - (2 * FRAME_INSET));
  const radius = Math.max(1, Math.round(Math.min(box.w, box.h) * FRAME_SEARCH_RADIUS));
  const diamond = frameRatios(pixels, perimeterPoints([[cx, cy - hh], [cx + hw, cy], [cx, cy + hh], [cx - hw, cy]]), radius);
  // 빨간 테두리는 60% 로 충분하지만, 검은 테두리(흑백 인쇄)만으로 통과하려면 더 촘촘해야 한다(검은 글자·로고 오탐 방지)
  if (diamond.red >= FRAME_MIN_RATIO || diamond.red + diamond.dark >= FRAME_MIN_DARK_RATIO) return "diamond";
  const square = frameRatios(pixels, perimeterPoints([[cx - hw, cy - hh], [cx + hw, cy - hh], [cx + hw, cy + hh], [cx - hw, cy + hh]]), radius);
  if (square.red >= FRAME_MIN_RATIO) return "square";
  return null;
}

// ---------------------------------------------------------------------------
// 페이지 래스터(스캔본)에서 빨간 마름모 찾기
//
// 텍스트 레이어가 없는 스캔 PDF(실측: 600dpi 를 가로 띠 36장으로 넣은 문서, 150dpi 전체 페이지 한 장)는
// 그림문자가 페이지 그림의 일부라 이미지 단위 필터로는 절대 잡히지 않는다. 페이지를 성긴 격자로 훑어
// 빨간 픽셀의 연결 덩어리를 찾고, 정사각형에 가까우면서 속이 비어 있는(테두리만 빨간) 덩어리를 잘라 후보로 넣는다.
// ---------------------------------------------------------------------------
const BLOB_GRID = 600; // 연결 덩어리 탐색은 긴 변을 이 크기로 줄인 격자에서 한다(비용 고정)
const BLOB_MIN_CELLS = 10; // 격자에서 이보다 작은 덩어리는 글자/선으로 본다
const BLOB_MAX_PER_IMAGE = 12;
const BLOB_FILL_MIN = 0.08; // 덩어리 bbox 안에서 빨간 셀 비율: 마름모 테두리는 대략 0.1~0.5
const BLOB_FILL_MAX = 0.6; // 꽉 찬 빨간 사각형(로고·표 머리글)은 제외
const BLOB_MARGIN = 0.04; // 잘라낼 때 bbox 바깥으로 주는 여유
const BLOB_MAX_SPLIT = 9; // 맞닿은 덩어리를 등분하는 최대 개수(가로/세로 비율 상한)

function isRedPixel(data, i) {
  const r = data[i];
  const g = data[i + 1];
  const b = data[i + 2];
  return r >= 110 && g <= 120 && b <= 120 && r - Math.max(g, b) >= 50;
}

// 흑백 스캔(복합기 흑백 복사·1비트 스캔)의 마름모 테두리는 검다. 빨간 마름모가 하나도 없을 때만 이 기준으로 다시 찾는다.
function isDarkPixel(data, i) {
  return (0.299 * data[i]) + (0.587 * data[i + 1]) + (0.114 * data[i + 2]) < 100;
}

// 페이지 래스터에서 predicate(빨강 또는 검정)에 맞는 픽셀의 연결 덩어리 중 마름모 테두리처럼 생긴 것을 잘라낸다
function findDiamondBlobs(pixels, predicate) {
  const { data, width, height } = pixels;
  const step = Math.max(1, Math.ceil(Math.max(width, height) / BLOB_GRID));
  const gw = Math.ceil(width / step);
  const gh = Math.ceil(height / step);
  const mask = new Uint8Array(gw * gh);
  // 셀 안의 픽셀을 전부 보고 하나라도 맞으면 그 셀을 켠다(1~2px 가는 테두리가 격자에서 끊기지 않게 — 점 몇 개만 찍으면 놓친다)
  for (let y = 0; y < height; y++) {
    const gy = Math.floor(y / step) * gw;
    const row = y * width;
    for (let x = 0; x < width; x++) {
      const cell = gy + Math.floor(x / step);
      if (!mask[cell] && predicate(data, (row + x) * 3)) mask[cell] = 1;
    }
  }
  // 4-연결 flood fill 로 덩어리별 bbox 와 셀 수를 구한다
  const seen = new Uint8Array(gw * gh);
  const blobs = [];
  const stack = [];
  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || seen[start]) continue;
    let minX = gw;
    let minY = gh;
    let maxX = -1;
    let maxY = -1;
    let count = 0;
    stack.length = 0;
    stack.push(start);
    seen[start] = 1;
    while (stack.length) {
      const idx = stack.pop();
      const x = idx % gw;
      const y = Math.floor(idx / gw);
      count += 1;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      const neighbors = [idx - 1, idx + 1, idx - gw, idx + gw];
      if (x === 0) neighbors[0] = -1;
      if (x === gw - 1) neighbors[1] = -1;
      for (const n of neighbors) {
        if (n < 0 || n >= mask.length || seen[n] || !mask[n]) continue;
        seen[n] = 1;
        stack.push(n);
      }
    }
    const bw = maxX - minX + 1;
    const bh = maxY - minY + 1;
    if (bw < BLOB_MIN_CELLS || bh < BLOB_MIN_CELLS) continue;
    // 그림문자 여러 개가 꼭짓점끼리 맞닿아 한 덩어리로 잡히는 경우(실측: 마름모 3개가 173x58 셀 한 덩어리)는
    // 가로/세로 비율만큼 등분한다. 맞닿은 마름모는 크기가 같으므로 등분으로 충분하다.
    const parts = [];
    if (bw / bh > MAX_ASPECT && bw / bh <= BLOB_MAX_SPLIT) {
      const n = Math.round(bw / bh);
      const pw = bw / n;
      for (let k = 0; k < n; k++) parts.push({ minX: minX + Math.round(k * pw), minY, bw: Math.round(pw), bh, count: count / n });
    } else if (bh / bw > MAX_ASPECT && bh / bw <= BLOB_MAX_SPLIT) {
      const n = Math.round(bh / bw);
      const ph = bh / n;
      for (let k = 0; k < n; k++) parts.push({ minX, minY: minY + Math.round(k * ph), bw, bh: Math.round(ph), count: count / n });
    } else {
      parts.push({ minX, minY, bw, bh, count });
    }
    for (const part of parts) {
      const aspect = part.bw / part.bh;
      if (aspect > MAX_ASPECT || aspect < 1 / MAX_ASPECT) continue;
      const fill = part.count / (part.bw * part.bh);
      if (fill < BLOB_FILL_MIN || fill > BLOB_FILL_MAX) continue;
      blobs.push(part);
    }
  }
  blobs.sort((a, b) => b.count - a.count);
  const crops = [];
  for (const blob of blobs.slice(0, BLOB_MAX_PER_IMAGE)) {
    const mx = Math.round(blob.bw * step * BLOB_MARGIN);
    const my = Math.round(blob.bh * step * BLOB_MARGIN);
    const x0 = Math.max(0, (blob.minX * step) - mx);
    const y0 = Math.max(0, (blob.minY * step) - my);
    const x1 = Math.min(width, ((blob.minX + blob.bw) * step) + mx);
    const y1 = Math.min(height, ((blob.minY + blob.bh) * step) + my);
    if (x1 - x0 < MIN_SIZE || y1 - y0 < MIN_SIZE) continue;
    crops.push(cropPixels(pixels, x0, y0, x1 - x0, y1 - y0));
  }
  return crops;
}

// 빨간 마름모를 먼저 찾고, 하나도 없으면 검은 마름모(흑백 스캔)를 찾는다.
// 검은 덩어리는 글자·표 선도 섞여 오지만 크기·비율·채움 비율과 이후의 테두리 게이트·해시 거리에서 걸러진다.
function findRedDiamonds(pixels) {
  const red = findDiamondBlobs(pixels, isRedPixel);
  if (red.length) return red;
  return findDiamondBlobs(pixels, isDarkPixel);
}

function isPageLike(pixels) {
  return pixels.width >= PAGE_MIN_SIZE && pixels.height >= PAGE_MIN_SIZE;
}

// 합성/분할을 거친 픽셀 묶음을 후보 목록에 넣는다(크기·비율이 맞고 테두리가 있는 것만)
function pushCandidates(candidates, pageNum, name, pixels, stitched) {
  if (isPictogramShaped(pixels)) {
    const frame = frameTypeOf(pixels);
    if (frame) {
      candidates.push({ pageNum, name, pixels, stitched, frame });
      return;
    }
    // 정사각형에 가깝지만 테두리가 없는 큰 이미지(실측: 1240x1323 스캔 페이지)는 페이지로 보고 안에서 마름모를 찾는다
    if (!isPageLike(pixels)) return;
  }
  if (isWideStrip(pixels)) {
    for (const tile of splitWideImage(pixels)) {
      if (!isPictogramShaped(tile)) continue;
      const frame = frameTypeOf(tile);
      if (frame) candidates.push({ pageNum, name, pixels: tile, stitched, frame });
    }
    return;
  }
  if (isPageLike(pixels)) {
    const page = downscale(pixels, PAGE_MAX_SIZE);
    findRedDiamonds(page).forEach((crop, i) => {
      if (!isPictogramShaped(crop)) return;
      const frame = frameTypeOf(crop);
      if (frame) candidates.push({ pageNum, name: `${name}#blob${i + 1}`, pixels: crop, stitched, frame });
    });
  }
}

async function collectCandidateImages(pdf, maxPages) {
  const candidates = [];
  // 공통 이미지(g_: 머리글 로고 등)는 모든 페이지에 같은 이름으로 나오므로 단독 후보로는 한 번만 비교한다
  const seenSingles = new Set();
  const lastPage = maxPages > 0 ? Math.min(pdf.numPages, maxPages) : pdf.numPages;
  for (let pageNum = 1; pageNum <= lastPage; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const placed = await collectPlacedImages(page, pageNum);
    for (const group of groupPlacedImages(placed)) {
      if (group.items.length === 1) {
        const { name, img } = group.items[0];
        const key = name.startsWith("g_") ? name : `${pageNum}:${name}`;
        if (seenSingles.has(key)) continue;
        seenSingles.add(key);
        // 너무 작은 이미지(로고/장식)는 픽셀 변환 전에 걸러 메인 스레드 부담을 줄인다
        if (img.width < MIN_SIZE || img.height < MIN_SIZE) continue;
        const pixels = compositeToWhiteRGB(img.width, img.height, img.kind, img.data);
        if (pixels) pushCandidates(candidates, pageNum, name, pixels, 0);
        continue;
      }
      // 조각 묶음: 합친 크기가 그림문자 범위에 못 미치면 합성 비용을 들이지 않고 건너뛴다
      const scale = groupScale(group);
      if (group.rect.w * scale < MIN_SIZE || group.rect.h * scale < MIN_SIZE) continue;
      const pixels = compositeGroup(group, scale);
      const name = `${group.items[0].name}+${group.items.length - 1}`;
      pushCandidates(candidates, pageNum, name, pixels, group.items.length);
    }
  }
  return candidates;
}

// 크롭 창을 상하좌우 1칸씩 옮긴 9가지 해시를 만들어 레퍼런스마다 최소 거리를 쓴다.
// 저해상도(100px 안팎) 그림문자는 bbox 가 1~2px 어긋나기만 해도 그리드 전체가 한 칸 밀려
// 같은 그림인데도 거리 130 안팎이 나왔다(실측). 이동 허용으로 그 경우가 40 안팎으로 내려온다.
const SHIFTS = [-1, 0, 1];

// 크롭 안에 어두운 셀이 이보다 적으면 심벌이 없는 빈 마름모(실측: 스캔본의 빈 자리 표시용 마름모)로 보고 비교하지 않는다.
// 빈 마름모는 해시가 전부 0 이라 어두운 셀이 적은 레퍼런스와 우연히 가까워질 수 있다.
const MIN_DARK_CELLS = 40;

function matchOne(pixels) {
  const variants = [];
  for (const sy of SHIFTS) for (const sx of SHIFTS) variants.push(toBits(pixels, { shift: [sx, sy] }));
  const darkCells = (variants[4].match(/1/g) || []).length;
  if (darkCells < MIN_DARK_CELLS) return { id: null, blank: true, best: { id: null, dist: Infinity }, second: { id: null, dist: Infinity }, confident: false };
  // GHS_HASHES[id] 는 [웹앱 에셋 해시, ...변형 템플릿 해시] 배열이다(scripts/build-ghs-hashes.js)
  const scored = Object.entries(GHS_HASHES)
    .map(([id, refs]) => ({ id, dist: Math.min(...refs.map((ref) => Math.min(...variants.map((bits) => hamming(bits, ref))))) }))
    .sort((a, b) => a.dist - b.dist);
  const [best, second] = scored;
  const confident = best.dist <= MAX_DISTANCE && second.dist - best.dist >= MIN_MARGIN;
  return { id: confident ? best.id : null, best, second, confident };
}

// 진단용: 매칭 직전의 후보 목록(조각 합성·띠 분할을 거친 픽셀)을 그대로 돌려준다.
// 미인식 샘플을 분석할 때 후보가 어떻게 만들어졌는지 PNG 로 확인하는 데 쓴다(앱 코드에서는 호출하지 않음).
export async function collectPictogramCandidates(pdf, options = {}) {
  return collectCandidateImages(pdf, options.maxPages);
}

// 매칭에 실패한 후보(테두리는 있는데 심벌이 어떤 레퍼런스와도 안 맞는 마름모) 수를 함께 돌려준다.
// 빈 마름모(자리 표시용)는 그림문자가 아니므로 세지 않는다. 호출 쪽은 이 값으로 "일부만 인식" 을 판정한다.
const UNMATCHED_MIN_SIZE_RATIO = 0.6; // 인식된 그림문자 크기의 이 비율보다 작은 미인식 후보는 글자·장식으로 보고 세지 않는다

function matchCandidates(candidates, pictograms, matches) {
  const unmatchedSizes = [];
  let largestMatched = 0;
  for (const candidate of candidates) {
    const result = matchOne(candidate.pixels);
    if (!result.id && !result.blank) unmatchedSizes.push(Math.max(candidate.pixels.width, candidate.pixels.height));
    if (result.id) {
      largestMatched = Math.max(largestMatched, candidate.pixels.width, candidate.pixels.height);
      pictograms[result.id] = true;
      matches.push({
        pageNum: candidate.pageNum,
        stitched: candidate.stitched || 0,
        rendered: Boolean(candidate.rendered),
        ghsId: result.id,
        dist: result.best.dist,
        margin: result.second.dist - result.best.dist,
      });
    }
  }
  // 흑백 스캔에서는 글자 덩어리가 테두리 게이트를 간혹 통과한다(실측: 44px). 인식된 그림문자(150px 안팎)보다
  // 훨씬 작은 미인식 후보는 그림문자가 아니므로 "일부만 인식" 판정에서 뺀다.
  const minSize = largestMatched * UNMATCHED_MIN_SIZE_RATIO;
  return unmatchedSizes.filter((size) => size >= minSize).length;
}

// ---------------------------------------------------------------------------
// 벡터 그림문자 대비: 페이지 렌더링 후 스캔본과 같은 방법으로 빨간 마름모 찾기
//
// 그림문자를 이미지가 아니라 벡터 도형으로 그린 PDF(실측: 바스타, 절연유)는 이미지 객체가 없어 위 경로로는
// 절대 잡히지 않는다. 브라우저에서만(canvas 필요) 앞쪽 몇 페이지를 그려 페이지 래스터 경로로 넘긴다.
// 비용이 커서(페이지당 0.3~0.6초) 이미지 경로에서 아무것도 못 찾았고 호출 쪽이 허용한 경우에만 돈다.
// ---------------------------------------------------------------------------
const RENDER_PAGES = 3;
const RENDER_WIDTH = 1200;

async function collectRenderedPageCandidates(pdf, maxPages) {
  const candidates = [];
  const lastPage = Math.min(pdf.numPages, RENDER_PAGES, maxPages > 0 ? maxPages : RENDER_PAGES);
  for (let pageNum = 1; pageNum <= lastPage; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: RENDER_WIDTH / base.width });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    canvas.width = 0; // 메모리 반환
    canvas.height = 0;
    const pixels = compositeToWhiteRGB(imageData.width, imageData.height, 3, imageData.data);
    if (!pixels) continue;
    const before = candidates.length;
    pushCandidates(candidates, pageNum, `render:p${pageNum}`, pixels, 0);
    for (let i = before; i < candidates.length; i++) candidates[i].rendered = true;
  }
  return candidates;
}

// PDF 문서(PDFDocumentProxy)에서 그림문자로 보이는 이미지를 찾아 GHS ID로 매칭한다.
// 반환값: { pictograms: {GHS01: bool, ...}, matches: [{pageNum, ghsId, dist, margin}, ...] }
// options.maxPages 를 주면 앞쪽 N페이지만 훑는다(미지정 시 전체).
// options.renderFallback 이 true 이고 이미지에서 아무것도 못 찾았으면(브라우저에서만) 페이지를 렌더링해 다시 찾는다.
export async function matchPictogramsFromPdf(pdf, options = {}) {
  const candidates = await collectCandidateImages(pdf, options.maxPages);
  const pictograms = {};
  const matches = [];
  let unmatched = matchCandidates(candidates, pictograms, matches);

  if (!matches.length && options.renderFallback && typeof document !== "undefined") {
    try {
      unmatched = matchCandidates(await collectRenderedPageCandidates(pdf, options.maxPages), pictograms, matches);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("[pictogramImageMatcher] 페이지 렌더링 탐색 실패:", e);
    }
  }

  // unmatched: 그림문자처럼 생겼는데(빨간 테두리·심벌 있음) 어떤 그림문자와도 맞지 않은 후보 수.
  // 0 보다 크면서 matches 도 있으면 "일부만 인식" 이므로 자동 저장 대상에서 빼고 사람이 확인해야 한다.
  return { pictograms, matches, unmatched };
}
